import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, FileText } from "lucide-react";
import { Logo1895 } from "@/components/Logo1895";

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a0908] text-white">
      {/* Header */}
      <header className="border-b border-[#2a2826] bg-[#121110]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
          <Logo1895 variant="icon" width={32} />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Terms and Conditions</h1>
              <p className="text-gray-400">Last updated: March 2026</p>
            </div>
          </div>

          <div className="prose prose-invert max-w-none">
            <div className="bg-[#121110] border border-[#2a2826] rounded-2xl p-8 space-y-8">
              
              <section>
                <h2 className="text-xl font-semibold text-amber-400 mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-300 leading-relaxed">
                  By accessing and using 1895 ("the Service"), you agree to be bound by these Terms and Conditions. 
                  If you do not agree to these terms, please do not use the Service. 1895 is an AI-powered 
                  storyboarding platform designed for film and video production professionals.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-amber-400 mb-4">2. Description of Service</h2>
                <p className="text-gray-300 leading-relaxed">
                  1895 provides AI-powered tools for creating storyboards, including but not limited to:
                </p>
                <ul className="list-disc list-inside text-gray-300 mt-2 space-y-1">
                  <li>AI-generated storyboard frames and shot compositions</li>
                  <li>Character and location reference generation</li>
                  <li>Script analysis and shot breakdowns</li>
                  <li>3D location design and scene blocking</li>
                  <li>Team collaboration features</li>
                  <li>Project management tools</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-amber-400 mb-4">3. User Accounts</h2>
                <p className="text-gray-300 leading-relaxed">
                  To use certain features of the Service, you must create an account. You agree to:
                </p>
                <ul className="list-disc list-inside text-gray-300 mt-2 space-y-1">
                  <li>Provide accurate and complete registration information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Notify us immediately of any unauthorized access</li>
                  <li>Be responsible for all activities under your account</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mt-4">
                  You must be at least 13 years old to create an account. If you are under 18, you represent 
                  that you have parental or guardian consent to use the Service.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-amber-400 mb-4">4. User Content and Intellectual Property</h2>
                <p className="text-gray-300 leading-relaxed">
                  <strong className="text-white">Your Content:</strong> You retain all rights to content you upload, 
                  including scripts, reference images, and project materials. By using the Service, you grant us 
                  a limited license to process your content solely for providing the Service.
                </p>
                <p className="text-gray-300 leading-relaxed mt-4">
                  <strong className="text-white">Generated Content:</strong> AI-generated storyboard frames, 
                  character sheets, and other outputs created through the Service are owned by you for use in 
                  your projects. However, similar outputs may be generated for other users based on their inputs.
                </p>
                <p className="text-gray-300 leading-relaxed mt-4">
                  <strong className="text-white">Our Content:</strong> The Service, including its design, code, 
                  AI models, and documentation, remains our intellectual property.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-amber-400 mb-4">5. Acceptable Use</h2>
                <p className="text-gray-300 leading-relaxed">
                  You agree not to use the Service to:
                </p>
                <ul className="list-disc list-inside text-gray-300 mt-2 space-y-1">
                  <li>Generate content that is illegal, harmful, or violates third-party rights</li>
                  <li>Create deepfakes or misleading content depicting real individuals without consent</li>
                  <li>Generate explicit adult content or content exploiting minors</li>
                  <li>Violate copyright, trademark, or other intellectual property rights</li>
                  <li>Attempt to reverse engineer or extract our AI models</li>
                  <li>Resell or redistribute the Service without authorization</li>
                  <li>Interfere with the Service's operation or security</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-amber-400 mb-4">6. Privacy and Data</h2>
                <p className="text-gray-300 leading-relaxed">
                  Your privacy is important to us. We collect and process personal data in accordance with our 
                  Privacy Policy. Key points include:
                </p>
                <ul className="list-disc list-inside text-gray-300 mt-2 space-y-1">
                  <li>We collect information you provide during registration and use of the Service</li>
                  <li>Your project content is stored securely and not shared with third parties</li>
                  <li>We may use anonymized data to improve our AI models and Service</li>
                  <li>You can request deletion of your data at any time</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-amber-400 mb-4">7. Subscription and Payments</h2>
                <p className="text-gray-300 leading-relaxed">
                  Certain features may require a paid subscription. If you choose a paid plan:
                </p>
                <ul className="list-disc list-inside text-gray-300 mt-2 space-y-1">
                  <li>Fees are billed in advance on a monthly or annual basis</li>
                  <li>Subscriptions auto-renew unless cancelled before the renewal date</li>
                  <li>Refunds are provided in accordance with our refund policy</li>
                  <li>We may change pricing with 30 days notice</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-amber-400 mb-4">8. Disclaimers and Limitations</h2>
                <p className="text-gray-300 leading-relaxed">
                  <strong className="text-white">As-Is Service:</strong> The Service is provided "as is" without 
                  warranties of any kind, express or implied.
                </p>
                <p className="text-gray-300 leading-relaxed mt-4">
                  <strong className="text-white">AI Limitations:</strong> AI-generated content may contain 
                  inaccuracies, artifacts, or unexpected results. You are responsible for reviewing and 
                  validating all generated content before use in production.
                </p>
                <p className="text-gray-300 leading-relaxed mt-4">
                  <strong className="text-white">Liability:</strong> To the maximum extent permitted by law, 
                  we shall not be liable for any indirect, incidental, or consequential damages arising from 
                  your use of the Service.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-amber-400 mb-4">9. Termination</h2>
                <p className="text-gray-300 leading-relaxed">
                  We may suspend or terminate your access to the Service at any time for violation of these 
                  terms or for any other reason at our discretion. Upon termination, your right to use the 
                  Service ceases immediately, though provisions that by their nature should survive (such as 
                  intellectual property rights) will remain in effect.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-amber-400 mb-4">10. Changes to Terms</h2>
                <p className="text-gray-300 leading-relaxed">
                  We reserve the right to modify these terms at any time. We will notify you of significant 
                  changes via email or through the Service. Continued use after changes constitutes acceptance 
                  of the modified terms.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-amber-400 mb-4">11. Governing Law</h2>
                <p className="text-gray-300 leading-relaxed">
                  These terms shall be governed by and construed in accordance with applicable laws. Any disputes 
                  arising from these terms or your use of the Service shall be resolved through binding arbitration.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-amber-400 mb-4">12. Contact</h2>
                <p className="text-gray-300 leading-relaxed">
                  If you have questions about these terms, please contact us at:
                </p>
                <p className="text-gray-300 mt-2">
                  <strong className="text-white">Email:</strong> legal@1895.ai
                </p>
              </section>

            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
